import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Box,
  Input,
  InputGroup,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import formatStringByPattern from 'format-string-by-pattern';

import { useCommonOptionsStore } from '../../store';
import { useTranslations } from '../../translations/useTranslations';

import type { InputControlProps } from './types';

const formatInput = (format: string, cardNumber: string) =>
  formatStringByPattern(format, cardNumber.replace(/\D/g, ''));

export const InputControl = React.forwardRef<HTMLInputElement, any>(
  (
    {
      name: fieldName,
      label,
      helper,
      error,
      isDisabled,
      isRequired,
      mask,
      rules,
      ...props
    }: InputControlProps,
    forwardRef,
  ) => {
    const { register, getValues, setValue } = useFormContext();

    const lang = useCommonOptionsStore(state => state.lang);
    const [t] = useTranslations(lang);

    const registerOptions = {
      ...(isRequired && { required: t('requiredField') }),
      ...rules,
      onBlur: () => {
        if (props.onBlur) props.onBlur(fieldName as unknown as any);
      },
      onChange: () => {
        if (mask) {
          const val = getValues(fieldName);
          setValue(fieldName, formatInput(mask, val));
        }
        if (props.onChange) props.onChange(fieldName as unknown as any);
      },
    };

    const { ref: inputRef, ...rest } = register(fieldName, {
      ...registerOptions,
    });

    const mergedRef = (e: any) => {
      inputRef(e);
      if (forwardRef) (forwardRef as any).current = e;
    };

    return (
      <Box>
        <FormControl
          isInvalid={!!error}
          isDisabled={isDisabled}
          isRequired={isRequired}
          className={cx('InputContainer')}
        >
          <FormLabel className={cx('InputLabel')}>{label}</FormLabel>
          <InputGroup>
            <Input
              className={cx('Input')}
              {...props}
              {...(rest as any)}
              ref={mergedRef}
            />
          </InputGroup>

          {helper && !error && (
            <FormHelperText className={cx('InputHelper')}>
              {helper}
            </FormHelperText>
          )}
          {error && (
            <FormErrorMessage className={cx('InputError')}>
              {error as any}
            </FormErrorMessage>
          )}
        </FormControl>
      </Box>
    );
  },
);
